import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export default function SuccessPage() {
  const { profile, refreshProfile } = useAuth();
  const [ready, setReady] = useState(false);
  const pollCount = useRef(0);
  const timeoutRef = useRef(null);

  // Start polling on mount
  useEffect(() => {
    const poll = () => {
      refreshProfile();
      pollCount.current += 1;

      if (pollCount.current < MAX_POLL_ATTEMPTS) {
        timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        setReady(true);
      }
    };

    poll();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [refreshProfile]);

  // Stop polling when profile is updated to a paid tier
  useEffect(() => {
    if (profile?.subscription_tier && profile.subscription_tier !== 'free_trial') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setReady(true);
    }
  }, [profile?.subscription_tier]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        <p className="text-muted-foreground">Activating your subscription...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        <div className="bg-card rounded-xl shadow-card p-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
          >
            <CheckCircle className="w-20 h-20 text-success mx-auto mb-6" />
          </motion.div>

          <h1 className="text-title font-display text-primary mb-4">
            Payment Successful!
          </h1>

          <p className="text-muted-foreground mb-8">
            Thank you for subscribing to AskAIQ. Your account has been upgraded
            and you can now start your market research.
          </p>

          <Link
            to="/"
            className="inline-block w-full py-3 rounded-lg font-medium bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all"
          >
            Start Researching
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
